/**
 * How submissions are linked into people, for the whole organisation, and
 * what of the stored people layer is live on this server.
 *
 * Each stream can override the linking mode on its own settings. Admins
 * change these; anyone else who sees people reads them, so a question such
 * as "why were these two linked?" can be answered without an admin.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Group, Loader, NumberInput, Select, SimpleGrid, Stack, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import { useIdentitySettings, useSaveIdentitySettings } from '../../api/people';
import { useAuth } from '../../auth/AuthProvider';
import { formatDateTime, formatNumber } from '../../theme';
import { useVocab } from '../../vocab';
import { linkModeOptions } from '../people/peopleShared';

export function PeopleSettingsTab() {
  const v = useVocab();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'super_admin';
  const settings = useIdentitySettings(true);
  const save = useSaveIdentitySettings();
  const [mode, setMode] = useState('suggest');
  const [phoneMode, setPhoneMode] = useState('off');
  const [lookback, setLookback] = useState<number | ''>(0);
  const data = settings.data;

  useEffect(() => {
    if (!data) return;
    setMode(data.mode);
    setPhoneMode(data.phone_mode);
    setLookback(data.lookback_days);
  }, [data]);

  if (settings.isPending) return <Group justify="center" p="xl"><Loader /></Group>;
  if (settings.error || !data) {
    return <Alert color="red" variant="light">{settings.error?.message ?? 'Not available.'}</Alert>;
  }

  const dirty = mode !== data.mode || phoneMode !== data.phone_mode || (lookback === '' ? 0 : lookback) !== data.lookback_days;
  const options = linkModeOptions(v);
  const layer = data.layer;

  async function submit() {
    try {
      await save.mutateAsync({ mode, phone_mode: phoneMode, lookback_days: lookback === '' ? 0 : Number(lookback) });
      notifications.show({ color: 'teal', title: 'Saved', message: `${v.One('person')} linking updated.` });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not saved', message: (e as Error).message });
    }
  }

  return (
    <Stack maw={760}>
      <Card withBorder radius="md" padding="lg">
        <Stack>
          <div>
            <Text fw={600}>{`Linking ${v.many('lead')} into ${v.many('person')}`}</Text>
            <Text size="sm" c="dimmed">
              {`When ${v.a('lead')} arrives sharing an email address or phone number with an earlier one, it can join that ${v.one('person')}, be suggested for someone to check, or be left alone. Each ${v.one('campaign')} can choose differently on its own settings.`}
            </Text>
            {data.is_default && <Text size="xs" c="dimmed" mt={4}>Nothing saved yet: these are the defaults.</Text>}
          </div>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <Select
              label="By email"
              description="The same address as an earlier one"
              data={options}
              value={mode}
              onChange={(m) => setMode(m ?? 'suggest')}
              allowDeselect={false}
              disabled={!canEdit}
            />
            <Select
              label="By phone"
              description="The same number; never more eager than by email"
              data={options}
              value={phoneMode}
              onChange={(m) => setPhoneMode(m ?? 'off')}
              allowDeselect={false}
              disabled={!canEdit}
            />
          </SimpleGrid>
          <NumberInput
            label="Look back (days)"
            description={`Only link to ${v.many('lead')} this recent. 0 looks back over all time.`}
            min={0}
            value={lookback}
            onChange={(n) => setLookback(n === '' ? '' : Number(n))}
            disabled={!canEdit}
            maw={240}
          />
          {canEdit ? (
            <Group justify="flex-end">
              <Button onClick={submit} disabled={!dirty} loading={save.isPending}>Save</Button>
            </Group>
          ) : (
            <Text size="xs" c="dimmed">Only an admin can change these.</Text>
          )}
        </Stack>
      </Card>

      <Card withBorder radius="md" padding="lg" data-testid="people-layer">
        <Stack gap="sm">
          <Text fw={600}>On this server</Text>
          <Status label={`Stored ${v.many('person')}`} on={layer.stored} offText="Off: grouped by email" />
          <Status label={`New ${v.many('lead')} linked as they arrive`} on={layer.linking_live} />
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm">{`Existing ${v.many('lead')} given ids`}</Text>
            <Text size="sm" c={layer.backfilled_at ? undefined : 'dimmed'}>
              {layer.backfilled_at ? formatDateTime(layer.backfilled_at) : 'Not yet'}
            </Text>
          </Group>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm">Suggestions waiting</Text>
            <Anchor component={Link} to="/people/suggestions" size="sm">{formatNumber(layer.open_suggestions)}</Anchor>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

function Status({ label, on, offText = 'Off' }: { label: string; on: boolean; offText?: string }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text size="sm">{label}</Text>
      <Badge variant="light" color={on ? 'chartGreen' : 'gray'} radius="sm" tt="none">{on ? 'On' : offText}</Badge>
    </Group>
  );
}
